import {
  Component,
  forwardRef,
  Input,
  OnInit,
  Optional,
  Host,
  SkipSelf,
  OnDestroy
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  Validator,
  UntypedFormGroup,
  UntypedFormControl,
  Validators,
  ValidationErrors,
  ControlContainer,
  AbstractControl
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { FormFields } from '../../../shared/services/form-fields-generator.service';
import {
  BankService,
  IBankInfo
} from '../../../transfers/services/bank.service';
import { FormAllFieldsValidationService } from '../../../shared/services/form-all-fields-validation.service';
import TRANSFER_TYPE from '../../../transfers/enums/transfer-type';
import {
  StateService,
  IFinalState,
  IState
} from '../../../transfers/services/state.service';
import { IBillPayees } from '../../../transfers/services/to-accounts.service';

@Component({
  selector: 'ab-bank-list',
  templateUrl: './bank-list.component.html',
  styleUrls: ['./bank-list.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => BankListComponent)
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => BankListComponent),
      multi: true
    }
  ]
})
export class BankListComponent
  implements OnInit, Validator, ControlValueAccessor, OnDestroy {
  @Input() formFields: FormFields;
  @Input() formControlName: string;
  @Input() withoutState: boolean;
  @Input() withoutLabel: boolean;
  @Input() skipDestroy: boolean;

  public bankList: IBankInfo[];
  public onTouched: Function;
  public onChange: Function;
  public selectedBank: IBankInfo;
  private prefilledValue: string;
  protected transferType: TRANSFER_TYPE;
  private isAba = false;

  public bankFormGroup = new UntypedFormGroup({
    domesticBeneficiaryBank: new UntypedFormControl(null, Validators.required)
  });
  protected subscriptions: { [key: string]: Subscription } = {};

  protected formControl: AbstractControl;

  get domesticBeneficiaryBank() {
    return this.bankFormGroup.get('domesticBeneficiaryBank');
  }

  constructor(
    @Optional()
    @Host()
    @SkipSelf()
    protected controlContainer: ControlContainer,
    protected stateService: StateService,
    protected bankService: BankService,
    protected formAllFieldsValidationService: FormAllFieldsValidationService
  ) {}

  ngOnInit() {
    this.formControl = this.controlContainer.control.get(this.formControlName);
    this.formControl.enable();
    this.formAllFieldsValidationService.addSubForms(this.bankFormGroup);

    if (this.withoutState) {
      this.bankService.getBankByCountry().subscribe(banks => {
        this.bankList = banks;
        this.updateForm(0);
      });

      return;
    }

    this.stateService.setProp(IState.domesticBeneficiaryBank, {});

    this.subscriptions.valueChanges = this.domesticBeneficiaryBank.valueChanges.subscribe(
      value => {
        if (value) {
          this.stateService.setProp(IState.domesticBeneficiaryBank, value);
        }
      }
    );

    this.subscriptions.stateSubscription = this.stateService
      .getState()
      .subscribe(state => this.updateState(state));
  }

  updateState(state: IFinalState) {
    if (state && state.transferType) {
      this.transferType = state.transferType;
      this.bankService.getBankByCountry().subscribe(banks => {
        this.bankList = banks;
        this.updateForm();
      });
    }
  }

  updateForm(transferType?: TRANSFER_TYPE) {
    const type =
      transferType || transferType === 0 ? transferType : this.transferType;

    if (this.prefilledValue) {
      const prefilledBank = this.bankList.filter(bank => {
        if (
          this.isAba &&
          bank.abaCode.toLocaleLowerCase() ===
            this.prefilledValue.toLocaleLowerCase()
        ) {
          return bank;
        }

        if (bank.bicCode.toLowerCase() === this.prefilledValue.toLowerCase()) {
          return bank;
        }
      })[0];

      this.selectBank(prefilledBank);
      if (type !== TRANSFER_TYPE.default) {
        this.prefilledValue = null;
      }

      return;
    }

    if (type === TRANSFER_TYPE.billPayee) {
      const toAccount = this.stateService.getPropValue(IState.toAccount)
          .value as IBillPayees,
        arubaBank = this.bankList.filter(bank => {
          if (
            toAccount.bicCode &&
            bank.bicCode.toLowerCase() === toAccount.bicCode.toLowerCase()
          ) {
            return bank;
          }
        });

      this.selectBank(arubaBank[0]);
    } else {
      this.bankFormGroup.reset();
    }
  }

  selectBank(bank: any) {
    this.bankFormGroup.controls['domesticBeneficiaryBank'].setValue(bank);
    this.stateService.setProp(IState.domesticBeneficiaryBank, bank);
  }

  ngOnDestroy() {
    this.stateService.setProp(IState.domesticBeneficiaryBank, {});

    Object.keys(this.subscriptions).forEach(subscription => {
      this.subscriptions[subscription].unsubscribe();
    });

    if (!this.skipDestroy) {
      this.formControl.clearValidators();
      this.formControl.disable();
      this.formControl.markAsPristine();
      this.formControl.markAsUntouched();
      this.formControl.patchValue(null);
    }
  }

  writeValue(value) {
    if (!value) {
      this.selectedBank = value;
      this.prefilledValue = null;
      this.bankFormGroup.reset(
        { domesticBeneficiaryBank: null },
        { emitEvent: false }
      );
    } else {
      this.prefilledValue = value.bicCode;

      if (value.value) {
        this.prefilledValue = value.value;

        if (value.code && value.code === 'ABA') {
          this.isAba = true;
        }
      }

      if (this.bankList && this.bankList.length !== 0) {
        this.updateForm(0);
      }
    }
  }

  registerOnChange(fn: any) {
    this.domesticBeneficiaryBank.valueChanges.subscribe(fn);
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  validate(): ValidationErrors | null {
    if (this.bankFormGroup.valid) {
      return null;
    }

    if (
      this.bankFormGroup.invalid &&
      this.domesticBeneficiaryBank.errors &&
      this.domesticBeneficiaryBank.errors.required
    ) {
      return { required: true };
    }
  }
}
